import { html, type TemplateResult } from '@design.estate/dees-element';
import type { IHarnessToolCall } from '../interfaces.js';
import type { IHarnessToolRenderHelpers } from '../toolregistry.js';
import {
  harnessIsRecord,
  harnessLanguageForPath,
  harnessParseTodos,
  harnessStringifyValue,
  harnessSummarizeValue,
} from '../harness.helpers.js';
import '../dees-harness-todos/dees-harness-todos.js';

/**
 * Built-in tool-card bodies, one per registry kind. Kept as render functions
 * (not elements): they share the card chrome and differ only in body markup.
 * Custom tools override via IHarnessToolDescriptor.renderBody.
 */

const inputRecord = (call: IHarnessToolCall): Record<string, unknown> =>
  harnessIsRecord(call.input) ? call.input : {};

const outputText = (call: IHarnessToolCall): string => {
  if (call.errorText) return call.errorText;
  return harnessStringifyValue(call.output);
};

const filePathOf = (call: IHarnessToolCall): string => {
  const input = inputRecord(call);
  return String(input.path ?? input.filePath ?? input.file ?? '');
};

const renderTerminal = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const command = String(input.command ?? '');
  const output = call.output;
  let stdout = '';
  let stderr = '';
  let exitCode: number | undefined;
  if (harnessIsRecord(output)) {
    stdout = typeof output.stdout === 'string' ? output.stdout : harnessStringifyValue(output.stdout ?? '');
    stderr = typeof output.stderr === 'string' ? output.stderr : '';
    exitCode = typeof output.exitCode === 'number' ? output.exitCode : undefined;
  } else {
    stdout = harnessStringifyValue(output);
  }
  return html`
    ${command ? helpers.monoBlock(`$ ${command}`) : ''}
    ${stdout ? helpers.monoBlock(stdout) : ''}
    ${stderr ? helpers.monoBlock(stderr, { tone: 'error' }) : ''}
    ${call.errorText ? helpers.monoBlock(call.errorText, { tone: 'error' }) : ''}
    ${exitCode !== undefined
      ? helpers.keyValues([['exit code', String(exitCode)]])
      : ''}
  `;
};

const renderFileRead = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const path = filePathOf(call);
  const content = outputText(call);
  if (!content) return helpers.keyValues([['file', path || '—']]);
  return helpers.codeBlock(content, harnessLanguageForPath(path), path.split('/').pop() || undefined);
};

const renderFileWrite = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const path = filePathOf(call);
  const newContent = String(input.content ?? input.newContent ?? '');
  const previousContent = String(input.oldContent ?? input.originalContent ?? input.before ?? '');
  const filename = path.split('/').pop() || undefined;
  const language = harnessLanguageForPath(path);
  if (previousContent) {
    return helpers.diffBlock(previousContent, newContent, { language, filename });
  }
  if (newContent) {
    return helpers.codeBlock(newContent, language, filename);
  }
  return html`
    ${helpers.keyValues([['file', path || '—']])}
    ${outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderFileDelete = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  return html`
    ${helpers.keyValues([['file', filePathOf(call) || '—']])}
    ${outputText(call) ? helpers.monoBlock(outputText(call), call.status === 'error' ? { tone: 'error' } : undefined) : ''}
  `;
};

const renderDirList = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const output = call.output;
  const entries = Array.isArray(output)
    ? output.map((entry) => String(entry))
    : outputText(call).split('\n').filter(Boolean);
  return helpers.monoBlock(entries.join('\n'));
};

const renderBrowser = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const action = String(input.action ?? 'snapshot');
  const pairs: Array<[string, string]> = [['action', action]];
  if (input.url) pairs.push(['url', String(input.url)]);
  if (input.selector) pairs.push(['selector', String(input.selector)]);
  const output = call.output;
  let screenshotBase64 = '';
  if (harnessIsRecord(output)) {
    screenshotBase64 = String(output.screenshotBase64 ?? output.screenshot ?? output.dataBase64 ?? '');
  }
  const script = String(input.script ?? '');
  return html`
    ${helpers.keyValues(pairs)}
    ${script ? helpers.codeBlock(script, 'javascript') : ''}
    ${screenshotBase64 ? helpers.image(screenshotBase64, 'image/png', 'browser screenshot') : ''}
    ${!screenshotBase64 && outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderHttp = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const pairs: Array<[string, string]> = [];
  if (input.url) pairs.push(['url', String(input.url)]);
  const body = input.body !== undefined ? harnessStringifyValue(input.body) : '';
  const response = outputText(call);
  return html`
    ${pairs.length ? helpers.keyValues(pairs) : ''}
    ${body ? helpers.codeBlock(body, 'json') : ''}
    ${response ? helpers.codeBlock(response, 'json') : ''}
  `;
};

const renderJson = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const source = String(input.jsonString ?? '');
  return html`
    ${source ? helpers.codeBlock(source, 'json') : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call), call.isError || call.status === 'error' ? { tone: 'error' } : undefined) : ''}
  `;
};

const renderSearch = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const summary = harnessSummarizeValue(call.input);
  return html`
    ${summary ? helpers.monoBlock(summary) : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderSubtask = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const input = inputRecord(call);
  const prompt = String(input.prompt ?? input.description ?? '');
  return html`
    ${prompt ? helpers.monoBlock(prompt) : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call)) : ''}
  `;
};

const renderTodo = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const todos = harnessParseTodos(call.input);
  if (!todos.length) return helpers.monoBlock(harnessSummarizeValue(call.input) || outputText(call));
  return html`<dees-harness-todos compact .todos=${todos}></dees-harness-todos>`;
};

const renderMcp = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  return html`
    ${call.contentBlocks?.length ? helpers.contentBlocks(call.contentBlocks) : ''}
    ${call.structuredContent !== undefined
      ? helpers.codeBlock(harnessStringifyValue(call.structuredContent), 'json')
      : ''}
    ${!call.contentBlocks?.length && call.structuredContent === undefined && outputText(call)
      ? helpers.monoBlock(outputText(call), call.isError ? { tone: 'error' } : undefined)
      : ''}
    ${call.input !== undefined && harnessSummarizeValue(call.input)
      ? helpers.monoBlock(harnessSummarizeValue(call.input))
      : ''}
  `;
};

const renderUnknown = (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers): TemplateResult => {
  const inputSummary = harnessSummarizeValue(call.input);
  const inputJson = !inputSummary && call.input !== undefined ? harnessStringifyValue(call.input) : '';
  return html`
    ${inputSummary ? helpers.monoBlock(inputSummary) : ''}
    ${inputJson ? helpers.codeBlock(inputJson, 'json') : ''}
    ${outputText(call) ? helpers.monoBlock(outputText(call), call.status === 'error' ? { tone: 'error' } : undefined) : ''}
  `;
};

export const builtinBodyRenderers: Record<
  string,
  (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers) => TemplateResult
> = {
  'terminal': renderTerminal,
  'file-read': renderFileRead,
  'file-write': renderFileWrite,
  'file-delete': renderFileDelete,
  'dir-list': renderDirList,
  'browser': renderBrowser,
  'http': renderHttp,
  'json': renderJson,
  'search': renderSearch,
  'subtask': renderSubtask,
  'todo': renderTodo,
  'mcp': renderMcp,
  'unknown': renderUnknown,
};
